import { AuthStorage } from "utils/auth-storage";
import { React, useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { Button, message, Modal } from "antd";
import Container from "components/container";
import LoadingComponent from "components/loading-component";
import Text from "components/text";
import { SideBarEndService } from "apis/Services/SideBarService";
import styles from "./index.module.css";
import { Gantt, Willow } from "@svar-ui/react-gantt";
import "@svar-ui/react-gantt/all.css";
import EditTaskModal from "./edit-task-modal";

function ViewDependencies() {
  const { company_id, id } = useParams();
  const [isLoading, setisLoading] = useState(true);
  const [accessLevel, setAccessLevel] = useState(); // Assuming access level state exists
  const [projectName, setProjectName] = useState("Loading Project...");
  const [tasks, setTasks] = useState([]);
  const [links, setLinks] = useState([]);
  const [ganttApi, setGanttApi] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [modalTaskName, setModalTaskName] = useState("");

  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const [month, day, year] = dateStr.split("-");
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  };

  const transformDataToGantt = (dependenciesDict) => {
    const tasksMap = new Map();
    const linksArray = [];
    let linkId = 1;

    Object.entries(dependenciesDict).forEach(([taskId, taskData]) => {
      const { info } = taskData;

      const startDate = parseDate(info.startDate); // can be null
      const endDate = parseDate(info.endDate); // can be null

      let start;
      let end;

      // CASE 1: start date is missing
      if (!startDate) {
        start = new Date(); // today
        end = new Date(start.getTime() + 24 * 60 * 60 * 1000); // today + 1 day
      } else {
        start = startDate;

        // CASE 2: start exists, end is missing → end = today
        if (!endDate) {
          end = new Date(); // today
        } else {
          end = endDate;
        }

        // Extra guard: if end before start → use today or +1 day fallback
        if (end <= start) {
          end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
        }
      }

      tasksMap.set(parseInt(taskId), {
        id: parseInt(taskId),
        text: info.specTitle,
        start,
        end,
        progress: info.completed ? 100 : 0,
        type: info.completed ? "summary" : "task",
      });
    });

    const linksSet = new Set();

    Object.entries(dependenciesDict).forEach(([taskId, taskData]) => {
      const { predecessors = [] } = taskData;
      const tid = parseInt(taskId);

      predecessors.forEach((child) => {
        const cid = parseInt(child.id);
        if (cid !== tid) {
          const key = `${cid}->${tid}`;

          if (!linksSet.has(key)) {
            linksSet.add(key);
            linksArray.push({
              id: `link-${linkId++}`,
              source: cid,
              target: tid,
              type: "e2s",
            });
          }
        }
      });
    });

    return { tasks: Array.from(tasksMap.values()), links: linksArray };
  };

  const [dependenciesData, setDependenciesData] = useState({});

  function getProcurementLogRowDependencies() {
    let code = AuthStorage.getItem("token");
    let email = AuthStorage.getItem("email");
    let ReqObj = {
      company_id: company_id,
      id: id,
      code,
      loginEmail: email,
    };
    SideBarEndService.getProcurementLogRowDependencies(ReqObj)
      .then((res) => {
        setProjectName(res.projectName || "Project Dependencies");

        setDependenciesData(res.dependenciesDict);

        setAccessLevel(3);

        const { tasks: transformedTasks, links: transformedLinks } =
          transformDataToGantt(res.dependenciesDict || {});

        setTasks(transformedTasks);
        setLinks(transformedLinks);
        setisLoading(false);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Failed to load dependencies");
        setisLoading(false);
        setAccessLevel(0);
      });
  }

  useEffect(() => {
    setisLoading(true);
    getProcurementLogRowDependencies();
  }, [company_id, id]);

  // const handleModalCancel = () => {
  //   setIsCustomModalVisible(false);
  //   setEditingTask(null);
  //   setModalTaskName("");
  // };

  const handleCreateDependencies = async (link) => {
    let code = AuthStorage.getItem("token");
    let email = AuthStorage.getItem("email");

    console.log(link, "LINK DETAILS");

    const Data = {
      code,
      loginEmail: email,
      company_id,
      id: id,
      entryID: link?.source,
      successorEntryIDs: [link.target],
      predecessorEntryIDs: [],
    };

    try {
      await SideBarEndService.createProcurementLogRowDependencies(Data);
      message.success("Dependencies saved successfully!");
    } catch (err) {
      console.log("Error saving dependencies:", err);
      message.error("Something went wrong, please try again.");
    } finally {
      getProcurementLogRowDependencies();
    }
  };

  const [ganttApiReference, setGanttApiReference] = useState(null);

  function init(api) {
    setGanttApiReference(api);
    setGanttApi(api);
  }

  // Intercepts and API updates
  useEffect(() => {
    if (!ganttApi) return;

    // When user creates a new dependency
    ganttApi.intercept("add-link", (ev) => {
      console.log(ev, "added link!");

      handleCreateDependencies(ev.link);
      setLinks((prev) => [
        ...prev,
        {
          id: `new-${Date.now()}-${Math.random()}`,
          source: ev.link.source,
          target: ev.link.target,
        },
      ]);
      // TODO: send to backend
    });

    // When user deletes a dependency
    ganttApi.intercept("delete-link", (ev) => {
      setLinks((prev) => prev.filter((l) => l.id !== ev.id));
      // TODO: update backend
    });

    // When user attempts to drag a task: Return false to prevent the action
    ganttApi.intercept("drag-task", (ev) => {
      console.log("Task drag attempt blocked for ID:", ev.id);
      // Returning false prevents the default drag behavior and update.
      // TODO: update backend
      return false;
    });

    ganttApi.intercept("delete-task", (ev) => {
      console.log("Task delete attempt blocked for ID:", ev.id);
      // Returning false prevents the default drag behavior and update.
      // TODO: update backend
      return false;
    });

    // When user clicks to show the editor (Task/Summary click)
    ganttApi.intercept("show-editor", (ev) => {
      if (ev) {
        const fullTaskData = ganttApiReference.getTask(ev.id);
        console.log("Full Task Data:", fullTaskData);
        setEditingTask(ev.id);
        setModalTaskName(fullTaskData.text);
        setIsDrawerOpen(true);

        return false;
      }
      return true;
    });
  }, [ganttApi, ganttApiReference]);

  const [isWeeklyScale, setIsWeeklyScale] = useState(false);

  const dailyScales = [
    { unit: "month", step: 1, format: "MMMM yyyy" },
    { unit: "day", step: 1, format: "d" },
  ];

  const weeklyScales = [
    { unit: "month", step: 1, format: "MMMM yyyy" },
    {
      unit: "week",
      step: 1,
      format: (date) => {
        const weekNumber = Math.ceil(date.getDate() / 7);
        return `W${weekNumber}`;
      },
    },
  ];

  const gridColumns = [
    { id: "text", header: "Submittal", flexgrow: 1 },
    {
      id: "start",
      header: "Contract Issued Release",
      width: 200,
      align: "center",
      template: (task) => {
        if (!task) return "-";
        const d = new Date(task);
        return `${String(d.getMonth() + 1).padStart(2, "0")}-${String(
          d.getDate()
        ).padStart(2, "0")}-${d.getFullYear()}`;
      },
    },
  ];

  const apiRef = useRef(null);

  return (
    <Container className="mt-3">
      <EditTaskModal
        open={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          // setEditingTask(null);
          // setModalTaskName("");
        }}
        taskId={editingTask}
        taskName={modalTaskName}
        dependenciesData={dependenciesData}
        getProcurementLogRowDependencies={getProcurementLogRowDependencies}
      />

      {isLoading ? (
        <LoadingComponent />
      ) : accessLevel < 2 ? (
        <div style={{ textAlign: "center" }}>
          <Text>
            You do not have the necessary permissions to access this page.
          </Text>
        </div>
      ) : (
        <>
          <div className=" bg-white rounded-4 p-3 mb-4">
            <div className=" d-flex flex-row justify-content-between mb-3">
              <div>
                <Text className="fs-5 fw-bold opacity-75 d-flex align-items-center gap-2">
                  Gantt Chart
                </Text>

                {projectName !== "Project Dependencies" && (
                  <Text className="text-muted fs-4">{projectName}</Text>
                )}
              </div>

              <Button
                type="default"
                onClick={() => setIsWeeklyScale((prev) => !prev)}
              >
                Switch to {isWeeklyScale ? "Weekly View" : "Daily View"}
              </Button>
            </div>

            {/* Legend */}
            <div className="d-flex gap-4 mt-3">
              <div className="d-flex align-items-center gap-2">
                <div
                  style={{
                    width: 20,
                    height: 10,
                    background: "#4a90e2",
                    borderRadius: 3,
                  }}
                />
                <span className="text-muted">Incomplete Item</span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <div
                  style={{
                    width: 20,
                    height: 10,
                    background: "#4caf50",
                    borderRadius: 3,
                  }}
                />
                <span className="text-muted">Completed Item</span>
              </div>
            </div>

            {/* Instructions */}
            <div
              className="mt-3 p-3 rounded-4"
              style={{ background: "#f7f7f7" }}
            >
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>Double-click an item to open the editor drawer.</li>
                <li>
                  Hover over an item to reveal the dots on each end — click a
                  dot, then click a dot on another item to create a dependency.
                </li>
              </ul>
            </div>
          </div>

          <div className="row p-3">
            {tasks.length > 0 ? (
              <Willow
                tools={["link"]}
                toolbar={{
                  add: false,
                  edit: false,
                  delete: false,
                  link: false,
                }}
              >
                <div className={styles.demo}>
                  <div className={styles.ganttContainer}>
                    <Gantt
                      tasks={tasks}
                      links={links}
                      scales={isWeeklyScale ? dailyScales : weeklyScales}
                      init={init}
                      columns={gridColumns}
                      autoScale={true}
                      ref={apiRef}
                    />
                  </div>
                </div>
              </Willow>
            ) : (
              <div style={{ textAlign: "center", padding: "20px" }}>
                <Text>No dependencies found for this project.</Text>
              </div>
            )}
          </div>
        </>
      )}
    </Container>
  );
}

export default ViewDependencies;
